import { useState, useEffect, useRef } from 'react';

/**
 * Retry Detection State
 * Tracks retry behavior inferred from job polling patterns
 */
export interface RetryState {
  /** Whether we're currently in a retry cycle */
  isRetrying: boolean;
  /** Number of retry cycles detected */
  retryCount: number;
  /** Number of consecutive polls with stalled progress */
  stallPollCount: number;
  /** Last observed progress value */
  lastProgress: number;
  /** Timestamp when progress last changed */
  lastProgressChangeTime: number;
}

/**
 * useRetryDetection Hook
 *
 * Detects retry behavior by analyzing job polling patterns:
 * - If progress stays unchanged for >10s during processing = retry likely happening
 * - Increments retry count after each detected stall cycle
 * - Resets when progress changes (retry completed)
 *
 * This is a frontend-only solution that infers backend retry state
 * without requiring backend communication.
 *
 * @param jobId - Current job ID
 * @param status - Current job status ('processing', 'completed', etc.)
 * @param progress - Current progress percentage (0-100)
 * @param getNow - Optional function to get current time (for testing)
 * @returns RetryState object with retry detection info
 *
 * @example
 * ```typescript
 * const retryState = useRetryDetection(job.id, job.status, job.progress);
 *
 * if (retryState.retryCount >= 2) {
 *   toast.warning('Processing slower than usual...');
 * }
 * ```
 */
export function useRetryDetection(
  jobId: string,
  status: string,
  progress: number,
  getNow: () => number = () => Date.now()
): RetryState {
  const [retryState, setRetryState] = useState<RetryState>({
    isRetrying: false,
    retryCount: 0,
    stallPollCount: 0,
    lastProgress: -1,
    lastProgressChangeTime: getNow(),
  });

  // Track previous values to detect changes
  const prevJobId = useRef(jobId);
  const prevStatus = useRef(status);
  const hasNotifiedForCycle = useRef(false);

  // Effect runs on every render to check for stalls
  // This is necessary because time can pass without any prop changes (polling)
  // Safe from infinite loops because setState calls check if values actually changed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Reset everything if not processing
    if (status !== 'processing') {
      // Only update if status actually changed (to prevent infinite loops)
      if (prevStatus.current === 'processing' && status !== 'processing') {
        setRetryState({
          isRetrying: false,
          retryCount: 0,
          stallPollCount: 0,
          lastProgress: -1,
          lastProgressChangeTime: getNow(),
        });
      }
      hasNotifiedForCycle.current = false;
      prevJobId.current = jobId;
      prevStatus.current = status;
      return;
    }

    // Job changed - reset state
    if (jobId !== prevJobId.current) {
      setRetryState({
        isRetrying: false,
        retryCount: 0,
        stallPollCount: 0,
        lastProgress: progress,
        lastProgressChangeTime: getNow(),
      });
      hasNotifiedForCycle.current = false;
      prevJobId.current = jobId;
      prevStatus.current = status;
      return;
    }

    const now = getNow();

    // First time seeing this job - initialize
    if (retryState.lastProgress === -1) {
      setRetryState({
        isRetrying: false,
        retryCount: 0,
        stallPollCount: 0,
        lastProgress: progress,
        lastProgressChangeTime: now,
      });
      prevJobId.current = jobId;
      prevStatus.current = status;
      return;
    }

    // Progress changed - retry cycle completed
    if (progress !== retryState.lastProgress) {
      setRetryState(prev => ({
        ...prev,
        isRetrying: false,
        lastProgress: progress,
        lastProgressChangeTime: now,
        stallPollCount: 0,
      }));
      hasNotifiedForCycle.current = false;
      prevJobId.current = jobId;
      prevStatus.current = status;
      return;
    }

    // Progress stalled - check if it's been long enough to consider it a retry
    const stallDuration = now - retryState.lastProgressChangeTime;
    const STALL_THRESHOLD_MS = 10000; // 10 seconds

    if (stallDuration > STALL_THRESHOLD_MS && !retryState.isRetrying) {
      // Detected a new retry cycle
      setRetryState(prev => ({
        ...prev,
        isRetrying: true,
        retryCount: prev.retryCount + 1,
      }));
      hasNotifiedForCycle.current = false;
    }

    prevJobId.current = jobId;
    prevStatus.current = status;
  });

  return retryState;
}

/**
 * Helper function to format retry message for toast notifications
 *
 * @param retryCount - Number of retry cycles detected
 * @returns Formatted message string
 */
export function formatRetryMessage(retryCount: number): string {
  if (retryCount === 2) {
    return 'Processing is taking longer than usual. This is normal when AI providers are busy.';
  } else if (retryCount >= 3 && retryCount < 5) {
    return `Processing continues (attempt ${retryCount}). Your images are still being processed in the background.`;
  } else if (retryCount >= 5) {
    return `Processing is experiencing delays (attempt ${retryCount}). This may take a few more minutes. Your job will complete successfully.`;
  }
  return 'Processing...';
}
